import { DatePipe } from '@angular/common';
import { LocalizationModule } from '@abp/ng.core';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { KanbanCardBodyComponent } from '@digiwall/goliath-core';
import { PlanningDataSource } from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { BasePlanningCardComponent } from '../base-planning-card.component';
import { PlanningDesiredDateComponent } from '../../planning-desired-date.component';
import { ProductionStepPlanningItemFieldsDto } from './production-step-planning-item-fields';

/**
 * Item card for the ProductionStep planning data source. Rendered by the board through NgComponentOutlet
 * with only the generic item dto; the specifics are read from the typed `displayFields` payload.
 *
 * Layout (inverse of the base ProductionFolder card): the client is the muted reference (top-left) and
 * the step status resolves to the header dot/pill — both via the shared card-body chrome. The
 * production-folder number is the emphasised heading, with a right-aligned "Step X" tag on the same row
 * (the step's order within the folder) so a planner can lay the steps out in sequence — ordering is not
 * enforced by the backend.
 */
@Component({
  selector: 'document-planning-production-step-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
  imports: [KanbanCardBodyComponent, LocalizationModule, PlanningDesiredDateComponent],
  template: `
    <core-kanban-card-body
      [reference]="fields().customer"
      [statusEnum]="store.workOrderStatus()"
      [status]="dto().statusId"
      [statusEditable]="statusEditable()"
      [selectableStatuses]="store.workOrderStatus()"
      [statusItemId]="statusItemId()"
    >
      <div class="kanban-card-body__heading d-flex align-items-center gap-2">
        <span>{{ fields().displayNumber }}</span>
        <span class="kanban-card-body__badge align-self-center mt-0 ms-auto">
          {{ 'Doc::ProductionFolderLine:Step' | abpLocalization }} {{ fields().reference }}
        </span>
      </div>

      @if (isVisible('stepName') && fields().stepName; as stepName) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Doc::ProductionFolderLine:Step' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ stepName }}</span>
        </div>
      }
      @if (isVisible('equipment') && fields().equipment; as equipment) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{
            'Product::Equipment:Equipment' | abpLocalization
          }}</span>
          <span class="kanban-card-body__line-value">{{ equipment }}</span>
        </div>
      }
      @if (isVisible('worker') && fields().worker; as worker) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ 'Contact::Worker' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ worker }}</span>
        </div>
      }
      @if (isVisible('manufacturer') && fields().manufacturer; as manufacturer) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ '::Manufacturer' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ manufacturer }}</span>
        </div>
      }
      @if (isVisible('responsible') && fields().responsible; as responsible) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ '::Responsible' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ responsible }}</span>
        </div>
      }
      @if (isVisible('schedule') && scheduleLabel(); as scheduleLabel) {
        <div class="kanban-card-body__line">
          <span class="kanban-card-body__line-label">{{ 'Doc::EndProdDate' | abpLocalization }}</span>
          <span class="kanban-card-body__line-value">{{ scheduleLabel }}</span>
        </div>
      }
      @if (dto().requestedDate) {
        <div class="kanban-card-body__line d-flex">
          <document-planning-desired-date
            class="ms-auto"
            [date]="dto().requestedDate"
            [critical]="requestedDateCritical()"
          />
        </div>
      }
    </core-kanban-card-body>
  `,
})
export class ProductionStepPlanningCardComponent extends BasePlanningCardComponent<ProductionStepPlanningItemFieldsDto> {
  protected readonly store = inject(GoliathDocumentsEnumerationTypeStore);
  private readonly datePipe = inject(DatePipe);
  protected readonly dataSource = PlanningDataSource.ProductionStep;

  protected readonly scheduleLabel = computed(() => {
    const date = this.fields().preferredProductionDate;
    return date ? (this.datePipe.transform(date, 'd MMM y') ?? undefined) : undefined;
  });
}
